import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAutomationLogs } from '@/lib/db'
import type { AutomationLog } from '@/types'
import { TRIGGER_META, ACTION_META } from './flowConstants'
import {
  Loader2, ChevronLeft, Activity, CheckCircle2, XCircle, AlertTriangle, Clock
} from 'lucide-react'

export default function FlowLogs() {
  const navigate = useNavigate()
  const [logs, setLogs] = useState<AutomationLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const { data } = await getAutomationLogs(undefined, 200)
      if (data) setLogs(data)
      setLoading(false)
    }
    load()
  }, [])

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" /></div>
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin/flows')} className="text-sm text-gray-500 hover:text-gray-900 mb-3 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Volver a flujos
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="w-7 h-7 text-blue-400" />
          Historial de ejecuciones
        </h1>
        <p className="text-gray-500 mt-1">{logs.length} ejecuciones registradas</p>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">Sin ejecuciones</p>
          <p className="text-gray-500 mt-1">Cuando un flujo se dispare, aparecerá aquí.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="text-left px-4 py-3 font-medium">Estado</th>
                <th className="text-left px-4 py-3 font-medium">Flujo</th>
                <th className="text-left px-4 py-3 font-medium">Trigger</th>
                <th className="text-left px-4 py-3 font-medium">Acciones</th>
                <th className="text-left px-4 py-3 font-medium">Alumno</th>
                <th className="text-left px-4 py-3 font-medium">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const flow = log.flow as any
                const trigger = TRIGGER_META[log.trigger_type]
                const actionsExecuted = log.actions_executed || []
                return (
                  <tr key={log.id} className="border-b border-gray-200/50 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {log.status === 'success' ? (
                        <span className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" /> OK</span>
                      ) : log.status === 'error' ? (
                        <span className="flex items-center gap-1 text-red-600" title={log.error_message || ''}><XCircle className="w-4 h-4" /> Error</span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-700"><AlertTriangle className="w-4 h-4" /> Parcial</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium">{flow?.name || log.flow_id.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{trigger?.icon} {trigger?.label || log.trigger_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {actionsExecuted.map((a: any, i: number) => {
                          const meta = ACTION_META[a.action_type]
                          return <span key={i} title={meta?.label || a.action_type} className="text-base">{meta?.icon || '?'}</span>
                        })}
                        {actionsExecuted.length === 0 && <span className="text-gray-500">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{log.student_id.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-gray-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(log.executed_at).toLocaleString('es-CL')}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
